/**
 * Inbox utility functions for date formatting and message preview truncation
 */

/**
 * Format a timestamp for inbox display
 * Rules:
 * - < 1 hour → Xm (e.g., "5m")
 * - < 24 hours → Xh (e.g., "3h")
 * - < 7 days → Xd (e.g., "2d")
 * - Else → formatted date (e.g., "Jan 15")
 * 
 * @param {string|Date|number|{seconds: number, nanoseconds: number}} timestamp - The timestamp to format
 * @returns {string} - Formatted time string
 */
export const formatInboxTime = (timestamp) => {
    if (!timestamp) return '';

    let date;

    // Handle Firestore Timestamp object
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        return '';
    }

    // Validate date
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Less than 1 hour
    if (diffMinutes < 60) {
        return diffMinutes <= 0 ? 'now' : `${diffMinutes}m`;
    }

    // Less than 24 hours
    if (diffHours < 24) {
        return `${diffHours}h`;
    }

    // Less than 7 days
    if (diffDays < 7) {
        return `${diffDays}d`;
    }

    // Format as date (e.g., "Jan 15" or "Jan 15, 2024" if different year)
    const options = { month: 'short', day: 'numeric' };
    if (date.getFullYear() !== now.getFullYear()) {
        options.year = 'numeric';
    }

    return date.toLocaleDateString('en-US', options);
};

/**
 * Format a timestamp for message bubble display (more descriptive than formatInboxTime)
 * Rules:
 * - < 1 minute → "Just now"
 * - < 60 minutes → "Xm ago"
 * - < 24 hours → "Xh ago"
 * - Yesterday → "Yesterday HH:MM AM/PM"
 * - This year → "Mon DD" (e.g., "Feb 12")
 * - Older → "Mon DD, YYYY"
 * 
 * @param {string|Date|number|{seconds: number, nanoseconds: number}} timestamp - The timestamp to format
 * @returns {string} - Human-friendly time string
 */
export const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';

    let date;

    // Handle Firestore Timestamp object
    if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
        date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        return '';
    }

    // Validate date
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    // Less than 1 minute
    if (diffMinutes < 1) {
        return 'Just now';
    }

    // Less than 60 minutes
    if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
    }

    // Less than 24 hours
    if (diffHours < 24) {
        return `${diffHours}h ago`;
    }

    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear()
    ) {
        return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }

    // Same year
    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Different year
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Get the display label for a message source
 * @param {Object} message - Message object
 * @returns {string|null} - "Automation", "Broadcast", or null for manual
 */
export const getMessageSourceLabel = (message) => {
    if (!message) return null;
    if (message.source === 'automation') return 'Automation';
    if (message.isBroadcast) return 'Broadcast';
    return null;
};

/**
 * Truncate message text for preview display
 * Handles edge cases for attachments, empty messages, and system messages
 * 
 * @param {string} text - The message text to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 40)
 * @param {Object} options - Additional options
 * @param {boolean} options.hasAttachment - Whether the message has an attachment
 * @param {string} options.attachmentType - Type of attachment (image, video, audio, file)
 * @param {boolean} options.isSystem - Whether this is a system message
 * @returns {string} - Truncated preview text
 */
export const truncateMessagePreview = (text, maxLength = 40, options = {}) => {
    const { hasAttachment, attachmentType, isSystem } = options;

    // Handle attachments
    if (hasAttachment && !text) {
        switch (attachmentType?.toLowerCase()) {
            case 'image':
                return '📷 Image';
            case 'video':
                return '🎥 Video';
            case 'audio':
                return '🎵 Audio';
            case 'file':
            case 'document':
                return '📎 Attachment';
            default:
                return '📎 Media';
        }
    }

    // Handle empty message
    if (!text || typeof text !== 'string') {
        if (hasAttachment) return '📎 Attachment';
        return 'Start a conversation';
    }

    // Handle system messages
    if (isSystem) {
        const trimmed = text.trim();
        if (trimmed.length <= maxLength) return trimmed;
        return trimmed.substring(0, maxLength - 3).trim() + '...';
    }

    // Normal text truncation
    const trimmed = text.trim();
    if (trimmed.length <= maxLength) return trimmed;

    // Try to truncate at word boundary
    const truncated = trimmed.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    // If we found a space and it's not too far back, truncate there
    if (lastSpace > maxLength * 0.6) {
        return truncated.substring(0, lastSpace).trim() + '...';
    }

    return truncated.trim() + '...';
};

/**
 * Get display-ready conversation data with formatted time and truncated preview
 * 
 * @param {Object} conversation - Raw conversation object from Firebase
 * @returns {Object} - Conversation with formatted display fields
 */
export const formatConversationForDisplay = (conversation) => {
    if (!conversation) return null;

    const lastMessageText = conversation.lastMessage || conversation.preview || '';

    return {
        ...conversation,
        displayTime: formatInboxTime(conversation.lastUpdated || conversation.lastMessageAt),
        displayPreview: truncateMessagePreview(lastMessageText, 40, {
            hasAttachment: conversation.hasAttachment,
            attachmentType: conversation.attachmentType,
            isSystem: conversation.isSystemMessage,
        }),
        // Keep original values for reference
        _rawLastUpdated: conversation.lastUpdated || conversation.lastMessageAt,
        _rawLastMessage: lastMessageText,
    };
};

/**
 * Parse template variables in broadcast message
 * Variables: {{MyName}}, {{FirstName}}, {{LastName}}, {{FullName}}, {{Interested Property}}, {{MyPhone}}
 * 
 * @param {string} template - Message template with variables
 * @param {Object} lead - Lead data for variable replacement
 * @param {Object} userData - Current user data (for MyName, MyPhone)
 * @returns {string} - Message with variables replaced
 */
export const parseBroadcastTemplate = (template, lead, userData = {}) => {
    if (!template) return '';

    let message = template;

    // Lead variables
    message = message.replace(/\{\{FirstName\}\}/gi, lead?.firstName || lead?.name?.split(' ')[0] || '');
    message = message.replace(/\{\{LastName\}\}/gi, lead?.lastName || lead?.name?.split(' ').slice(1).join(' ') || '');
    message = message.replace(/\{\{FullName\}\}/gi, lead?.fullName || lead?.name || '');
    // Interested Property: join all properties from interestedProperties array (single source of truth)
    message = message.replace(/\{\{Interested Property\}\}/gi, (() => {
        const props = lead?.interestedProperties || [];
        if (props.length > 0) return props.map(p => p.address).filter(Boolean).join(', ');
        return '';
    })());

    // User / sender variables
    message = message.replace(/\{\{MyName\}\}/gi, userData?.displayName || userData?.fullName || userData?.name || '');
    message = message.replace(/\{\{MyPhone\}\}/gi, userData?.phone || '');

    return message;
};
